<template>
  <v-overlay
    :value="loading"
    :z-index="zIndex"
    :opacity="1"
    data-test="money-loader"
  >
    <v-card
      outlined
      class="money-loader"
      role="status"
      aria-live="polite"
    >
      <v-progress-circular
        indeterminate
        :size="68"
        :width="2"
        color="var(--tc-action)"
        class="money-loader__mark"
        data-test="money-loader-mark"
      >
        <span class="money-loader__icon">
          <PhCurrencyDollar
            :size="26"
            weight="bold"
          />
        </span>
      </v-progress-circular>

      <transition
        name="money-loader-fade"
        mode="out-in"
      >
        <div :key="displayMessage">
          <p
            class="money-loader__title"
            data-test="money-loader-title"
          >
            {{ displayMessage }}
          </p>
          <p
            v-if="subtitle"
            class="money-loader__subtitle"
          >
            {{ subtitle }}
          </p>
        </div>
      </transition>

      <div
        v-if="steps"
        class="money-loader__steps"
        aria-hidden="true"
      >
        <span
          v-for="step in steps"
          :key="step"
          class="money-loader__step"
          :class="{ 'money-loader__step--done': step - 1 <= currentStep }"
        ></span>
      </div>
    </v-card>
  </v-overlay>
</template>

<script>
import { PhCurrencyDollar } from 'phosphor-vue';

/**
 * Carregamento em modal central, no padrão dos modais do sistema (overlay com
 * --tc-scrim, cartão sem sombra, raio de overlay, ícone em círculo como na
 * ConfirmationWindow). Quando o processo tem etapas, mostra o subtítulo e o
 * progresso delas.
 */
export default {
  name: 'MoneyLoader',

  components: { PhCurrencyDollar },

  props: {
    loading: { type: Boolean, default: true },
    message: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    steps: { type: Number, default: 0 },
    currentStep: { type: Number, default: 0 },
    zIndex: { type: Number, default: 999 }
  },

  computed: {
    displayMessage() {
      return this.message || this.$t('comum.moneyLoaderUi.defaultMessage');
    }
  }
};
</script>

<style scoped>
.money-loader {
  width: 400px;
  max-width: calc(100vw - var(--tc-space-6));
  padding: var(--tc-space-6) var(--tc-space-5) var(--tc-space-5);
  border-radius: var(--tc-radius-overlay) !important;
  border-color: var(--tc-line) !important;
  background: var(--tc-surface) !important;
  font-family: var(--tc-font-body);
  text-align: center;
  user-select: none;
}

/* O ícone vai no slot do anel, e o Vuetify o centraliza. A margem precisa ser
   explícita: deep-layout/_styles.scss dá margem de 1rem a todo
   v-progress-circular, e ela tirava o anel do centro do ícone. */
.money-loader__mark {
  display: flex;
  margin: 0 auto var(--tc-space-4);
}

/* Mesmo círculo de ícone da ConfirmationWindow: 56px, cor de ação suavizada. */
.money-loader__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tc-action);
  background: var(--tc-action-tint-strong);
}

.money-loader__title {
  margin: 0;
  font-size: var(--tc-fs-h3);
  font-weight: var(--tc-fw-semibold);
  line-height: var(--tc-lh-tight);
  color: var(--tc-text);
}

.money-loader__subtitle {
  margin: var(--tc-space-1) 0 0;
  font-size: var(--tc-fs-sm);
  line-height: var(--tc-lh-body);
  color: var(--tc-text-2);
}

.money-loader__steps {
  display: flex;
  gap: var(--tc-space-1);
  margin-top: var(--tc-space-5);
}

.money-loader__step {
  flex: 1;
  height: var(--tc-space-1);
  border-radius: var(--tc-radius-sm);
  background: var(--tc-line);
  transition: background-color 0.3s ease;
}

.money-loader__step--done {
  background: var(--tc-action);
}

.money-loader-fade-enter-active,
.money-loader-fade-leave-active {
  transition: opacity 0.2s ease;
}

.money-loader-fade-enter,
.money-loader-fade-leave-to {
  opacity: 0;
}
</style>
